import React from "react";

import { isTypeError, isTypeString } from "utility/TypeUtils";

export type NotificationOptions = {
    buttonClass?: string;
    buttonIcon?: React.ReactNode;
    containerClass?: string;
    duration?: number;
    iconClass?: string;
    iconSrc?: string;
    removeFadeDuration?: number;
};

export type NotificationProps = {
    buttonClass?: string;
    buttonIcon?: React.ReactNode;
    containerClass?: string;
    iconClass?: string;
    iconSrc: string;
    id: string;
    message: Object | Object[];
    // Callbacks
    onClose?: () => void;
};

const Notification: React.FC<NotificationProps> = ({
    buttonClass = "",
    buttonIcon,
    containerClass = "",
    iconClass = "",
    iconSrc,
    id,
    message,
    // Callbacks
    onClose
}) => {

    const defaultButtonIcon = (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="fill-current w-5 h-5 text-gray-700"
            viewBox="0 0 20 20"
            fill="currentColor"
        >
            <title>Close</title>
            <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
        </svg>
    );

    const formatMessage = (message: Object | Object[]) => {
        if (isTypeString(message)) {
            return message;
        }
        else if (isTypeError(message)) {
            return message.stack.toString();
        }
        else if (Array.isArray(message)) {
            return message.map(
                (item: Object | Object[]) => {
                    if (isTypeString(item)) {
                        return item;
                    }
                    else if (isTypeError(item)) {
                        return item.stack.toString();
                    }
                    else {
                        return item ? JSON.stringify(item) : "Unknown error";
                    }
                }).join("\n");
        }
        else {
            return message ? JSON.stringify(message) : "Unknown error";
        }
    };

    const renderMessage = (formattedText: string, key?: number) => {
        const firstLineBreakIndex = formattedText.indexOf("\n");

        if (firstLineBreakIndex === -1) {
            return (
                <pre key={key} className="whitespace-pre-line break-words">
                    {formattedText}
                </pre>
            );
        }

        const firstLine = formattedText.substring(0, firstLineBreakIndex);
        const restOfText = formattedText.substring(firstLineBreakIndex + 1);

        return (
            <div key={key} className="flex flex-col w-full">
                <pre className="whitespace-nowrap overflow-x-auto">
                    {firstLine}
                </pre>
                <pre className="whitespace-pre-wrap break-words">
                    {restOfText}
                </pre>
            </div>
        );
    };

    return (
        <div
            id={id}
            className={`inline-block rounded-md border shadow-md w-auto max-w-full ${onClose ? "cursor-pointer" : ""} ${containerClass}`}
            onClick={onClose && onClose}
        >
            <div className="flex">
                {
                    (iconSrc) && (
                        <div className="flex items-center justify-center flex-shrink-0 p-2">
                            <img className={`h-6 w-6 ${iconClass}`} src={iconSrc} alt="icon"/>
                        </div>
                    )
                }
                {
                    message && (
                        <div className="p-2 text-sm font-normal overflow-hidden">
                            <div className="text-left w-full">
                                {
                                    (() => {
                                        if (Array.isArray(message)) {
                                            return message.map((item: Object | Object[], index: number) =>
                                                renderMessage(formatMessage(item), index)
                                            );
                                        }
                                        return renderMessage(formatMessage(message));
                                    })()
                                }
                            </div>
                        </div>
                    )
                }
                {
                    onClose && (
                        <div className="flex items-center justify-center flex-shrink-0 p-1">
                            <div className={`p-1 rounded-md ${buttonClass}`}>
                                {buttonIcon || defaultButtonIcon}
                            </div>
                        </div>
                    )
                }
            </div>
        </div>
    );
};

export default Notification;